import React, { useRef, useEffect } from 'react';
import { Layout, Drawer, Dropdown, Space, Typography, Button } from 'antd';
import { MenuOutlined, FilterOutlined, ReloadOutlined, DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Route, Filters } from '../../types/index';
import SkiTouringFilters from '../filters/SkiTouringFilters';
import { activityEmojis, activityNames } from '../../constants/activities';
import { useIsDesktop } from '../../hooks/common/useMediaQuery';

const { Sider } = Layout;
const { Text } = Typography;

interface SidebarProps {
  routes: Route[];
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  routes,
  filters,
  setFilters,
  sidebarOpen,
  setSidebarOpen
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const isDesktop = useIsDesktop();

  // Sauvegarder la position de scroll avant les changements
  const preserveScrollPosition = () => {
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }
  };

  // Restaurer la position de scroll après les changements
  useEffect(() => {
    if (scrollContainerRef.current && scrollPositionRef.current > 0) {
      scrollContainerRef.current.scrollTop = scrollPositionRef.current;
    }
  });

  const handleActivityChange = (activity: string): void => {
    preserveScrollPosition();
    setFilters(prev => ({
      ...prev,
      activities: [activity],
      skitouring: activity === 'skitouring' ? (prev.skitouring || {}) : undefined
    }));
  };

  const handleSkiTouringFiltersChange = (skiFilters: unknown): void => {
    preserveScrollPosition();
    setFilters(prev => ({
      ...prev,
      skitouring: skiFilters as Filters['skitouring']
    }));
  };

  const handleResetFilters = (): void => {
    preserveScrollPosition();
    setFilters(prev => ({
      ...prev,
      skitouring: undefined
    }));
  };

  const selectedActivity = filters.activities.length > 0 ? filters.activities[0] : 'skitouring';

  // Liste des activités disponibles
  const availableActivities = [
    'skitouring',
    'rock_climbing',
    'hiking',
    'mountain_climbing',
    'snow_ice_mixed',
    'ice_climbing',
    'snowshoeing',
    'mountain_biking',
    'via_ferrata',
    'slacklining'
  ];

  // Créer le menu dropdown pour les activités
  const activityMenuItems: MenuProps['items'] = availableActivities.map(activity => ({
    key: activity,
    label: (
      <Space>
        <span>{activityEmojis[activity] || '🎯'}</span>
        <span>{activityNames[activity] || activity}</span>
      </Space>
    ),
    onClick: () => {
      handleActivityChange(activity);
    }
  }));

  const SidebarContent = () => (
    <div ref={scrollContainerRef} style={{ height: '100%', overflowY: 'auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%', padding: '24px' }}>
        {/* Header avec logo */}
        <div style={{ textAlign: 'center', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
          <img
            src="/logo.png"
            alt="Logo"
            style={{ height: '48px', width: '100%', objectFit: 'contain', maxWidth: '100%' }}
          />
        </div>

        {/* Sélecteur d'activité */}
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text strong style={{ fontSize: '12px', textTransform: 'uppercase', color: '#8c8c8c' }}>
            <FilterOutlined style={{ marginRight: '8px' }} />
            Activité
          </Text>
          <Dropdown
            menu={{ items: activityMenuItems, selectedKeys: [selectedActivity] }}
            trigger={['click']}
            placement="bottomLeft"
          >
            <Button
              block
              style={{
                height: 'auto',
                padding: '12px',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Space>
                <span style={{ fontSize: '20px' }}>
                  {activityEmojis[selectedActivity] || '🎯'}
                </span>
                <Text strong>
                  {activityNames[selectedActivity] || selectedActivity}
                </Text>
              </Space>
              <DownOutlined />
            </Button>
          </Dropdown>
        </Space>

        {/* Filtres spécifiques selon l'activité */}
        {selectedActivity === 'skitouring' ? (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text strong style={{ fontSize: '12px', textTransform: 'uppercase', color: '#8c8c8c' }}>
              Filtres ski de randonnée
            </Text>
            <SkiTouringFilters
              filters={filters.skitouring || {}}
              onChange={handleSkiTouringFiltersChange}
              routes={routes}
            />
          </Space>
        ) : (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            background: '#fafafa',
            borderRadius: '8px',
            border: '1px dashed #d9d9d9'
          }}>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              🚧 Filtres en cours de développement
            </Text>
          </div>
        )}

        {/* Bouton pour réinitialiser les filtres */}
        {filters.skitouring && Object.keys(filters.skitouring).length > 0 && (
          <Button
            type="primary"
            danger
            block
            icon={<ReloadOutlined />}
            onClick={handleResetFilters}
          >
            Réinitialiser les filtres
          </Button>
        )}
      </Space>
    </div>
  );

  return (
    <>
      {/* Bouton hamburger flottant pour mobile */}
      {!isDesktop && (
        <Button
          type="default"
          icon={<MenuOutlined />}
          style={{
            position: 'fixed',
            top: '16px',
            left: '16px',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            background: 'white'
          }}
          onClick={() => setSidebarOpen(true)}
        />
      )}

      {/* Mobile sidebar avec Drawer */}
      <Drawer
        title="Filtres"
        placement="left"
        onClose={() => setSidebarOpen(false)}
        open={sidebarOpen && !isDesktop}
        width={320}
        styles={{ body: { padding: 0 } }}
        zIndex={1001}
      >
        <SidebarContent />
      </Drawer>

      {/* Static sidebar for desktop */}
      {isDesktop && (
        <Sider
          width={288}
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            overflow: 'auto',
            height: '100vh'
          }}
          theme="light"
        >
          <SidebarContent />
        </Sider>
      )}
    </>
  );
};

export default Sidebar;
